import { Popover, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import React from 'react';


export default function AboutHTML(props) {
  return (
    <div className="inline-block self-center">     
      <div className="overflow-hidden rounded-lg shadow-lg ">
        <div className="relative gap-8 bg-white ">
          <div className="p-4 bg-orange-100 flex items-center">
              <div className="text-sm font-medium text-gray-900">
                <div className="font-bold text-lg">Overview</div>
                  <div>
                    This screening tool is designed to assist users in evaluating the techno-economic feasibility of various processes involving micro and macroalgae. 
                    It helps identify algae characteristics, process specifics, and product target prices, and evaluates the prospects of a cascading approach for algal valorization. 
                    The tool relies on a comprehensive database of different projects and literature, providing users with valuable insights into the economic prospects of their processes. 
                  </div>

                <div className="font-bold text-lg mt-3">Algae characteristics </div>
                  <div>
                    The tool features a database that provides comprehensive information on various types of algae. 
                    It includes details such as species, classification, and phylum, along with their occurrence in either freshwater or saltwater environments. Additionally, the database outlines typical characteristics like dry matter content, and the levels of carbon, nitrogen, phosphorus, and ash content, as well as crude protein.<br/>
                    Moreover, it contains average specifications for total (crude) lipid content, carbohydrates, cellulose, starch, and various other compounds specific to each type of algae. These compounds include alginate, laminarin, fucoidan, ulvan, agar, and floridian starch. 
                  </div>

                <div className="font-bold text-lg mt-5">Product Target Prices</div>
                  <div>The tool provides target prices for various algal products based on literature and market data available.</div>

                <div className="font-bold text-lg mt-5">CAPEX calculation </div>
                  <div>To estimate the capital investment required for each process step, the tool gathers key information specific to that step, using data from literature and industry sources related to similar processes. This information encompasses total investment and capacity. It is crucial that both projects operate under similar principles and have comparable capacities. The tool applies a straightforward formula for investment estimation, incorporating a scaling factor to recognize that most process steps do not scale linearly.
                    <br/> In a simplified way per each process step a simple correlation like the following is applied: 
                    
                    <pre className='p-4 font-semibold'>
                      <em>Cost</em><sub className='text-green-700'><em>step</em><sub className='text-red-700'>i</sub></sub> = 
                      <em> Cost</em><sub className='text-green-700'><em>reference</em></sub> * (
                        <em>Capacity</em><sub className='text-green-700'><em>step</em><sub className='text-red-700'>i</sub></sub> / <em>Capacity</em><sub className='text-green-700'><em>reference</em></sub> 
                      )<sup className='text-red-700'><em>n</em></sup>  * (
                        <em>CEPCI</em><sub className='text-green-700'><em>actual</em></sub> / <em>CEPCI</em><sub className='text-green-700'><em>reference</em></sub> 
                      )
                    </pre>
                    
                    <br/> The reference CEPCI for this tool is 2023.   </div>

                <div className="font-bold text-lg mt-5">OPEX Calculation </div>
                  <div>Operational expenditures (OPEX) are estimated using typical industrial data and relevant literature related to specific process steps or closely associated processes. 
                    Users also have the option to modify the OPEX for each process by entering their own data, which may be based on direct external validation from industrial partners or detailed techno-economic assessments. <br/>
                    OPEX for each step includes various costs such as chemicals and feedstock, electricity, and heat. 
                    It also encompasses direct operating labor, direct supervision, maintenance labor, total direct labor, as well as indirect labor. 
                    Furthermore, OPEX covers a range of additional expenses, including payroll overhead, maintenance material costs, indirect material costs, property taxes and insurance, administrative and corporate expenses, and sales expenses.
                </div>

                <div className="font-bold text-lg mt-5">Economic indicators</div>
                  <h1 className="font-bold text-left">Levelized Cost of Production (LCOP)</h1>
                  <div>
                    Represents the average cost per unit of product over the facility’s lifetime. <br />
                    <b>Includes:</b> Capital investment (CAPEX), Fixed and Variable Operating Costs (OPEX). <br />
                    In this tool, <b>LCOP</b> is calculated as the weighted average cost across all final products obtained. Simplified formula: 

                    <pre className='p-4 font-semibold'>
                      LCOP =(CRF*TCI + FCOP + VCOP) / <em>Q</em><sub>product</sub>
                    </pre>

                </div>

                <h1 className="font-bold text-left mt-2">Capital Recovery Factor (CRF) </h1>
                  <div>
                    Converts upfront investment into annualized cost over the project’s life. <br />
                      <pre className='p-4 font-semibold'>
                      CRF = <em>k</em> / (1-(1 + <em>k</em>)<sup><em>−L</em></sup>)
                      </pre>
                    Where: 
                    <ul>
                      <li><b>k</b> = Discount rate</li>
                      <li><b>L</b> = Operational lifetime (years)</li>  
                    </ul>
                    
                </div>

                <h1 className="font-bold text-left mt-3">Return on Investment (ROI, pre-tax) </h1>
                  <div>
                    Measures profitability before taxes. Formula: <br />                                          
                    <pre className='p-4 font-semibold'>
                      ROI<sub className='text-green-700'><em>pre-tax</em></sub> = Cash Flow<sub className='text-green-700'><em>pre-tax</em></sub>*100% / Total Capital Investment
                    </pre>
                </div>

                <h1 className="font-bold text-left mt-2">Internal Rate of Return (IRR)  </h1>
                  <div>The discount rate at which the net present value (NPV) of all cash flows equals zero. Reflects the expected annual return of the investment. <br />                                          
                </div>
                
              </div>

        </div>
        </div>
      </div>
    </div>
  )
}